// Modo "Videos cortos" — ADR-2026-10-22-videos-masivos-diseno-de-produccion.
//
// Elegís productos de tu catálogo (hasta 50) y Atlas hace un video vertical de ~15 s de cada uno, con el estilo de los
// clips que más venden en Mercado Libre: mira las fotos, escribe el guion y, si querés, le pone voz en off argentina con
// subtítulos. 12 créditos por video, para todos los planes.
//
// La tanda la corre el backend (`/v1/videos/masivo`): esta pantalla elige, lanza y sigue el estado de cada video. El
// lote en curso se recuerda por empresa, así que si la persona recarga, la pantalla lo retoma.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ChevronRight, Clapperboard, Download, Loader2, Mic, Package, PenLine, Sparkles, Subtitles, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { descargarArchivo } from '../../../utils/descargarArchivo';
import { firmarGenerada } from '../../../lib/videoGenerado';
import { buildDownloadFileName } from '../../../utils/downloadFileName';
import { useRegistrarPantalla } from '../../../chat-atlas/PuenteDePantallaContext';
import type { ControladorDePantalla } from '../../../chat-atlas/puenteDePantalla';
import { describirVideosCortos } from '../puente/camposDeModo';
import { empresaRecordada, recordarEmpresa } from '../empresaDeLaSesion';
import { useSidebar } from '../../../context/SidebarContext';
import { useRubroMarcaActiva } from '../../../hooks/useRubroMarca';
import { CreditBadge } from '../../../components/CreditBadge';
import OptionSheet from '../../../components/opciones/OptionSheet';
import { fetchUserProducts } from '../productsApi';
import type { MockProductV2 } from '../mockData';
import { ProductsContext, ProductsLoadedContext } from '../productsContext';
import { PickProductPanel } from '../components/PickProductPanel';
import { PanelLateralModo } from '../components/PanelLateralModo';
import { nombreCortoModo } from '../modesCatalog';
import { ShellModoRedisenado } from './shared/ShellModoRedisenado';
import {
  CREDITOS_POR_VIDEO,
  MAX_INDICACION,
  MAX_PRODUCTOS,
  cancelarLote,
  costoDeLote,
  lanzarLote,
  loteRecordado,
  recordarLote,
  textoDeEtapa,
  verLote,
  videosListos,
  type EstadoDeLote,
  type VideoListo,
  type VozDeVideo,
} from './videosCortos/videosCortosApi';

const PATH_MODO = 'videos-cortos';
const RUTA = '/videos-cortos';
const NOMBRE_RESPALDO = 'Videos cortos';
/** Cada cuánto se mira el estado de la tanda. Un video tarda ~5 min: no hace falta más seguido. */
const CADA_MS = 8_000;

function Interruptor({ prendido, onCambiar, deshabilitado, etiqueta }: { prendido: boolean; onCambiar: () => void; deshabilitado?: boolean; etiqueta: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={prendido}
      aria-label={etiqueta}
      disabled={deshabilitado}
      onClick={onCambiar}
      className={`relative inline-flex h-[26px] w-[46px] shrink-0 items-center rounded-full transition-colors duration-200 disabled:opacity-40 ${
        prendido ? 'bg-brand-primary' : 'bg-black/[0.12]'
      }`}
    >
      <span
        className={`inline-block h-[22px] w-[22px] rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.15)] transition-transform duration-200 ${
          prendido ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  );
}

/** La fila de una opción: ícono + título + bajada + lo que va a la derecha (mismo alto que las cards de Recrear). */
function Fila({ Icono, titulo, bajada, derecha, children }: { Icono: typeof Mic; titulo: string; bajada: string; derecha?: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-[#00000009]">
      <div className="flex min-h-[76px] items-center gap-3 px-3 pr-3.5 py-3">
        <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[12px] bg-white shadow-sm">
          <Icono className="w-5 h-5 text-[#1644EB]/50" strokeWidth={1.75} />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-brand-black leading-tight">{titulo}</p>
          <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 leading-snug">{bajada}</p>
        </div>
        {derecha}
      </div>
      {children}
    </div>
  );
}

export default function VideosCortosMode() {
  const navigate = useNavigate();
  const { sidebarWidth } = useSidebar();
  const { rubro, marcaId, loaded: rubroCargado } = useRubroMarcaActiva();

  // ── Cuenta ────────────────────────────────────────────────────────────────
  const [companyId, setCompanyId] = useState<string | null>(() => empresaRecordada());
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user || cancelado) return;
      const { data: p } = await supabase.from('profiles').select('id_company').eq('id', auth.user.id).maybeSingle();
      if (cancelado) return;
      const empresa = (p as { id_company?: string | null } | null)?.id_company ?? null;
      setCompanyId(empresa);
      recordarEmpresa(empresa, auth.user.id);
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // ── Productos de la marca activa (los lee `PickProductPanel` del context) ──
  const [productos, setProductos] = useState<MockProductV2[]>([]);
  const [productosCargados, setProductosCargados] = useState(false);
  useEffect(() => {
    if (!companyId || !rubroCargado) return;
    let cancelado = false;
    fetchUserProducts(companyId, marcaId)
      .then((p) => {
        if (!cancelado) setProductos(p);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelado) setProductosCargados(true);
      });
    return () => {
      cancelado = true;
    };
  }, [companyId, marcaId, rubroCargado]);

  // ── Lo que se elige ───────────────────────────────────────────────────────
  const [elegidos, setElegidos] = useState<MockProductV2[]>([]);
  const [voz, setVoz] = useState<VozDeVideo | null>('mujer');
  const [subtitulos, setSubtitulos] = useState(true);
  const [conIndicacion, setConIndicacion] = useState(false);
  const [indicacion, setIndicacion] = useState('');
  const [panelCatalogo, setPanelCatalogo] = useState(false);
  const costo = costoDeLote(elegidos.length);

  // ── La tanda ──────────────────────────────────────────────────────────────
  const [loteId, setLoteId] = useState<string | null>(null);
  const [lote, setLote] = useState<EstadoDeLote | null>(null);
  const [listos, setListos] = useState<VideoListo[]>([]);
  const [lanzando, setLanzando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listosVistos = useRef(-1);
  const [bajando, setBajando] = useState<string | null>(null);

  // Al entrar, se retoma la tanda que quedó en curso (o la última, para ver sus videos).
  useEffect(() => {
    if (!companyId || loteId) return;
    const recordado = loteRecordado(companyId);
    if (recordado) setLoteId(recordado);
  }, [companyId, loteId]);

  // Seguimiento: el estado de cada video y, cuando hay listos nuevos, sus videos firmados.
  useEffect(() => {
    if (!loteId || !companyId) return;
    let cancelado = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Con la tanda terminada se sigue mirando sólo si faltan videos por traer (una lectura o una firma que falló),
    // con un tope para no quedar consultando para siempre.
    let reintentosAlTerminar = 0;
    const mirar = async () => {
      try {
        const e = await verLote(loteId);
        if (cancelado) return;
        if (!e) {
          // La tanda ya no existe (otra cuenta, o se borró): se olvida.
          recordarLote(companyId, null);
          setLoteId(null);
          setLote(null);
          return;
        }
        setLote(e);
        if (e.listos !== listosVistos.current) {
          const v = await videosListos(loteId, companyId).catch(() => null);
          if (cancelado) return;
          if (v) setListos(v);
          // Se da por visto recién cuando llegaron todos: si algo falló, la próxima vuelta los vuelve a pedir.
          if (v && v.length >= e.listos) listosVistos.current = e.listos;
        }
        const faltanVideos = e.listos !== listosVistos.current;
        if (!e.terminado) timer = setTimeout(mirar, CADA_MS);
        else if (faltanVideos && reintentosAlTerminar++ < 10) timer = setTimeout(mirar, CADA_MS * 2);
      } catch {
        if (!cancelado) timer = setTimeout(mirar, CADA_MS * 2);
      }
    };
    void mirar();
    return () => {
      cancelado = true;
      if (timer) clearTimeout(timer);
    };
  }, [loteId, companyId]);

  const enCurso = !!lote && !lote.terminado;
  const falta = elegidos.length === 0 ? 'Elegí al menos un producto.' : enCurso ? 'Esperá a que termine la tanda en curso.' : null;

  const lanzar = async () => {
    if (falta || lanzando || !companyId) return;
    setError(null);
    setLanzando(true);
    const nuevo = crypto.randomUUID();
    try {
      await lanzarLote(nuevo, elegidos.map((p) => p.id), { voz, subtitulos, indicacion: conIndicacion ? indicacion : '' });
      recordarLote(companyId, nuevo);
      listosVistos.current = -1;
      setListos([]);
      setLote(null);
      setLoteId(nuevo);
      window.dispatchEvent(new Event('atlas:credits-updated'));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo lanzar la tanda.');
    } finally {
      setLanzando(false);
    }
  };

  const cancelar = async () => {
    if (!loteId) return;
    setCancelando(true);
    try {
      await cancelarLote(loteId);
      listosVistos.current = -1;
      const e = await verLote(loteId);
      if (e) setLote(e);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cancelar.');
    } finally {
      setCancelando(false);
    }
  };

  const nuevaTanda = () => {
    if (companyId) recordarLote(companyId, null);
    setLoteId(null);
    setLote(null);
    setListos([]);
    setElegidos([]);
  };

  // ── Agente ────────────────────────────────────────────────────────────────
  useRegistrarPantalla(
    useMemo<ControladorDePantalla>(
      () => ({
        clave: PATH_MODO,
        describir: () =>
          describirVideosCortos({
            ruta: RUTA,
            productos: elegidos.map((p) => p.name),
            maxProductos: MAX_PRODUCTOS,
            voz,
            subtitulos: !!voz && subtitulos,
            instruccion: conIndicacion ? indicacion : '',
            maxInstruccion: MAX_INDICACION,
            creditosPorVideo: CREDITOS_POR_VIDEO,
            generando: enCurso,
          }),
        aplicar: (cambios) => {
          const aplicados: string[] = [];
          for (const c of cambios) {
            if (c.campo === 'voz_en_off' && typeof c.valor === 'string') setVoz(c.valor === 'mujer' || c.valor === 'hombre' ? c.valor : null);
            else if (c.campo === 'subtitulos' && typeof c.valor === 'boolean') setSubtitulos(c.valor);
            else if (c.campo === 'instruccion' && typeof c.valor === 'string') {
              setConIndicacion(!!c.valor.trim());
              setIndicacion(c.valor.slice(0, MAX_INDICACION));
            } else continue;
            aplicados.push(c.campo);
          }
          return { aplicados, fallidos: [] };
        },
      }),
      [elegidos, voz, subtitulos, conIndicacion, indicacion, enCurso],
    ),
  );

  // ── Panel ─────────────────────────────────────────────────────────────────
  const cardProductos = (
    <button
      type="button"
      onClick={() => setPanelCatalogo(true)}
      className="w-full min-h-[76px] flex items-center gap-3 rounded-xl bg-[#00000009] px-3 pr-3.5 py-3 text-left transition-colors hover:bg-[#ECEDF1] active:scale-[0.995]"
    >
      <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-lg bg-white overflow-hidden">
        {elegidos[0]?.imageUrl ? (
          <img src={elegidos[0].imageUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <Package className="w-5 h-5 text-[#3C3C43]/40" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-semibold text-brand-black leading-tight">Productos</p>
        <p className="text-[13px] text-[#3C3C43]/55 mt-0.5 leading-snug">
          {elegidos.length > 0 ? `${elegidos.length} elegido${elegidos.length === 1 ? '' : 's'} · un video de cada uno` : `Elegí hasta ${MAX_PRODUCTOS} de tu catálogo`}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 shrink-0 text-brand-black/80" />
    </button>
  );

  const bloqueVoz = (
    <Fila
      Icono={Mic}
      titulo="Voz en off"
      bajada={voz ? 'Atlas escribe el texto de cada producto' : 'Sólo música'}
      derecha={<Interruptor etiqueta="Voz en off" prendido={!!voz} onCambiar={() => setVoz((v) => (v ? null : 'mujer'))} />}
    >
      {voz && (
        <div className="px-3 pb-3 animate-paso-adelante">
          <div role="radiogroup" className="grid grid-cols-2 gap-1 rounded-[12px] bg-[#EEEFF3] p-1">
            {(['mujer', 'hombre'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={voz === v}
                onClick={() => setVoz(v)}
                className={`rounded-[9px] py-1.5 text-[14px] font-medium transition-[background-color,box-shadow,color] ${
                  voz === v ? 'bg-white text-brand-black shadow-[0_1px_3px_rgba(16,24,40,0.12)]' : 'text-[#3C3C43]/60 hover:text-brand-black'
                }`}
              >
                {v === 'mujer' ? 'Mujer' : 'Hombre'}
              </button>
            ))}
          </div>
        </div>
      )}
    </Fila>
  );

  const bloqueSubtitulos = (
    <Fila
      Icono={Subtitles}
      titulo="Subtítulos"
      bajada={voz ? 'Siguen la voz, palabra por palabra' : 'Necesitan la voz en off'}
      derecha={<Interruptor etiqueta="Subtítulos" prendido={!!voz && subtitulos} deshabilitado={!voz} onCambiar={() => setSubtitulos((s) => !s)} />}
    />
  );

  const bloqueIndicacion = (
    <Fila
      Icono={PenLine}
      titulo="Indicaciones"
      bajada={conIndicacion ? 'Para todos los videos de la tanda' : 'Opcional'}
      derecha={
        <Interruptor
          etiqueta="Indicaciones"
          prendido={conIndicacion}
          onCambiar={() =>
            setConIndicacion((v) => {
              if (v) setIndicacion('');
              return !v;
            })
          }
        />
      }
    >
      {conIndicacion && (
        <div className="mt-1 mx-3 rounded-[13px] bg-white overflow-hidden border border-neutral-200 px-4 py-3 mb-3 animate-paso-adelante">
          <textarea
            rows={3}
            value={indicacion}
            maxLength={MAX_INDICACION}
            onChange={(e) => setIndicacion(e.target.value)}
            placeholder="Ej: con luz cálida de tarde, en un lugar con plantas…"
            className="w-full text-[15px] text-brand-black placeholder:text-[#3C3C43]/30 bg-transparent resize-none focus:outline-none leading-relaxed"
          />
        </div>
      )}
    </Fila>
  );

  const errorBox = error ? (
    <p className="inline-flex items-start gap-1.5 text-[13px] text-[#B42318] leading-snug px-1">
      <AlertTriangle className="w-3.5 h-3.5 mt-[2px] shrink-0" />
      {error}
    </p>
  ) : null;

  const contenidoDelPanel = (
    <div className="flex flex-col gap-2.5">
      {cardProductos}
      {bloqueVoz}
      {bloqueSubtitulos}
      {bloqueIndicacion}
      {errorBox}
    </div>
  );

  const pie = (clase: string) => (
    <div className={clase}>
      <button
        type="button"
        onClick={lanzar}
        disabled={lanzando || !!falta}
        title={falta ?? undefined}
        className="w-full inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[15.5px] font-semibold shadow-sm transition-colors"
      >
        {lanzando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
        {elegidos.length > 1 ? `Crear ${elegidos.length} videos` : 'Crear video'}
        {elegidos.length > 0 && <CreditBadge credits={costo} tone="on-primary" size="sm" />}
      </button>
      {falta && <p className="mt-2 text-center text-[12.5px] text-[#3C3C43]/55">{falta}</p>}
    </div>
  );

  // ── La columna de la derecha: la tanda (o lo elegido, antes de lanzar) ─────
  const nombreDe = (varianteId: string | null) => productos.find((p) => p.id === varianteId)?.name ?? 'Producto';
  const fotoDe = (varianteId: string | null) => productos.find((p) => p.id === varianteId)?.imageUrl;

  // Un `<a download>` no sirve: el video está en otro dominio (Storage) y el navegador ignora el `download`. Se baja como
  // en el resto de la app (en iPhone, con la hoja de compartir).
  const bajar = async (listo: VideoListo, nombre: string) => {
    if (bajando) return;
    setBajando(listo.id);
    setError(null);
    try {
      // La URL de la pantalla vence a la hora: para bajar se firma de nuevo (si no se puede, se prueba con la que hay).
      const url = (await firmarGenerada(listo.ruta).catch(() => null)) ?? listo.url;
      await descargarArchivo({ url, fileName: buildDownloadFileName({ variantName: nombre, forceVideo: true }), esVideo: true });
    } catch {
      setError('No se pudo descargar el video. Probá de nuevo.');
    } finally {
      setBajando(null);
    }
  };

  const tarjetaDeVideo = (v: EstadoDeLote['videos'][number]) => {
    const listo = listos.find((l) => l.varianteId === v.variante_id);
    return (
      <div key={v.job_id} className="flex flex-col gap-1.5">
        <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[#F2F3F6]">
          {listo ? (
            <video src={listo.url} poster={listo.poster} controls playsInline preload="metadata" className="h-full w-full object-cover" />
          ) : (
            <>
              {fotoDe(v.variante_id) && <img src={fotoDe(v.variante_id)} alt="" className="h-full w-full object-cover opacity-40" />}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-3 text-center">
                {v.estado === 'generando' || v.estado === 'en_cola' ? (
                  <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                ) : v.estado === 'fallo' ? (
                  <AlertTriangle className="w-6 h-6 text-[#B42318]" />
                ) : (
                  <X className="w-6 h-6 text-[#3C3C43]/50" />
                )}
                <p className="text-[13px] font-semibold text-brand-black">{textoDeEtapa(v)}</p>
                {v.error && <p className="text-[12px] text-[#3C3C43]/70 leading-snug">{v.error}</p>}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-0.5">
          <p className="truncate text-[13px] font-medium text-brand-black">{nombreDe(v.variante_id)}</p>
          {listo && (
            <button
              type="button"
              onClick={() => void bajar(listo, nombreDe(v.variante_id))}
              disabled={!!bajando}
              className="shrink-0 text-[#3C3C43]/60 hover:text-brand-black disabled:opacity-50"
              aria-label="Descargar"
            >
              {bajando === listo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>
    );
  };

  const muestra = (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {lote ? (
        <div className="mx-auto max-w-[900px] pb-8">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-[19px] font-bold leading-tight text-brand-black">
                {lote.terminado ? 'Tu tanda terminó' : 'Haciendo tus videos'}
              </h2>
              <p className="text-[14.5px] text-[#3C3C43]/60 mt-1">
                {lote.listos} de {lote.total} listos
                {lote.fallidos ? ` · ${lote.fallidos} no salieron (no se cobran)` : ''}
                {lote.cancelados ? ` · ${lote.cancelados} cancelados` : ''}
                {!lote.terminado ? ' · cada video tarda unos 5 minutos y salen varios a la vez' : ''}
              </p>
            </div>
            {enCurso ? (
              <button
                type="button"
                onClick={cancelar}
                disabled={cancelando}
                className="rounded-xl border border-black/[0.08] px-4 py-2 text-[14px] font-medium text-brand-black hover:bg-black/[0.03] disabled:opacity-50"
                title="Frena los videos que todavía no empezaron. Los que ya están en marcha se terminan y se cobran."
              >
                {cancelando ? 'Cancelando…' : 'Cancelar los que faltan'}
              </button>
            ) : (
              <button type="button" onClick={nuevaTanda} className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2 text-[14px] font-semibold text-white hover:bg-brand-primary-hover">
                <Check className="w-4 h-4" />
                Hacer otra tanda
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{lote.videos.map(tarjetaDeVideo)}</div>
        </div>
      ) : loteId ? (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
        </div>
      ) : (
        <div className="mx-auto max-w-[640px] pt-6 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#F2F3F6]">
            <Clapperboard className="w-6 h-6 text-[#1644EB]/60" />
          </span>
          <h2 className="mt-4 text-[19px] font-bold leading-tight tracking-[-0.01em] text-brand-black">Un video de cada producto</h2>
          <p className="text-[15.5px] text-[#3C3C43]/60 leading-relaxed mt-1">
            Elegí tus productos y Atlas hace un video vertical de unos 15 segundos de cada uno, con el estilo de los clips que más
            venden en Mercado Libre. {CREDITOS_POR_VIDEO} créditos por video.
          </p>
          {elegidos.length > 0 && (
            <div className="mt-6 grid grid-cols-4 gap-2 sm:grid-cols-6">
              {elegidos.map((p) => (
                <div key={p.id} className="aspect-square overflow-hidden rounded-xl bg-[#F2F3F6]">
                  <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const nombreModo = nombreCortoModo(PATH_MODO, NOMBRE_RESPALDO);
  const migas = [{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: nombreModo }];

  const picker = (variant: 'desktop' | 'mobile') => (
    <PickProductPanel
      variant={variant}
      hideBack
      hideHeader
      initialSelectedIds={elegidos.map((p) => p.id)}
      onConfirm={(ps) => {
        setPanelCatalogo(false);
        setElegidos(ps.slice(0, MAX_PRODUCTOS));
        if (ps.length > MAX_PRODUCTOS) setError(`Podés elegir hasta ${MAX_PRODUCTOS} productos por tanda: quedaron los primeros ${MAX_PRODUCTOS}.`);
        else setError(null);
      }}
      onBack={() => setPanelCatalogo(false)}
      rubro={rubro}
      confirmLabel="Usar estos productos"
      footerSiempreVisible
      columnasFijas={variant === 'desktop' ? 3 : 2}
      nombreSobreLaFoto
      ocultarBloqueSubida
    />
  );

  return (
    <ProductsContext.Provider value={productos}>
      <ProductsLoadedContext.Provider value={productosCargados}>
        {/* ── MOBILE ── */}
        <div className="md:hidden flex min-h-[100dvh] flex-col bg-surface-mobile">
          <div className="flex-1 px-5 pt-4 pb-44">
            <h1 className="text-[24px] font-semibold leading-tight text-brand-black">{nombreModo}</h1>
            <p className="text-[14.5px] text-[#3C3C43]/60 mt-1 mb-5 leading-snug">Un video corto de cada producto, para Mercado Libre y tus redes.</p>
            {/* En el teléfono el panel no se ve con la tanda en pantalla: los errores (cancelar, descargar) van arriba. */}
            {lote || loteId ? <div className="mb-6">{errorBox && <div className="mb-3">{errorBox}</div>}{muestra}</div> : contenidoDelPanel}
          </div>
          {!lote && !loteId && pie('fixed inset-x-0 bottom-0 z-20 bg-white px-5 pt-3 pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)] border-t border-black/[0.06]')}
        </div>

        {/* ── DESKTOP ── */}
        <ShellModoRedisenado
          sidebarWidth={sidebarWidth}
          migas={migas}
          titulo={nombreModo}
          panel={
            <>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-5 pb-4">{contenidoDelPanel}</div>
              {pie('shrink-0 px-6 pt-3 pb-4 bg-white border-t border-black/[0.05]')}
            </>
          }
          panelLateral={({ contenedorRef, offsetIzquierdo }) => (
            <PanelLateralModo
              abierto={panelCatalogo}
              onCerrar={() => setPanelCatalogo(false)}
              titulo="Elegí de tu catálogo"
              offsetIzquierdo={offsetIzquierdo}
              contenedorRef={contenedorRef}
            >
              <div className="flex-1 min-h-0 flex flex-col [&>div]:flex-1 [&>div]:min-h-0">{picker('desktop')}</div>
            </PanelLateralModo>
          )}
          muestra={muestra}
        />

        <div className="md:hidden">
          <OptionSheet open={panelCatalogo} onClose={() => setPanelCatalogo(false)} title="Elegí de tu catálogo" hideAccept>
            {picker('mobile')}
          </OptionSheet>
        </div>
      </ProductsLoadedContext.Provider>
    </ProductsContext.Provider>
  );
}
